import * as React from 'react'
import { useCallback, useState } from 'react'

import { CreatorPermission } from '@api'

import OfIcon from '@/assets/icons/icon-of.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import type { Creator } from '@/modules/creators/types.ts'
import { StartOfWarningDialog } from '@/modules/creators/ui/Dialogs/StartOfWarningDialog.tsx'
import { useDesktopApp } from '@/modules/desktop/DesktopAppProvider.tsx'
import {
  sendStartSession,
  startOnlyFansProfile,
} from '@/modules/desktop/utils.ts'
import { classNames } from '@/utils/helpers.ts'

import { isActiveCreator } from '../utils'

export interface StartOnlyFansButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  creator: Creator
  openProxyConfigurationDialog: (firstLaunch: boolean) => void
}

export const StartOnlyFansButton = React.forwardRef<
  HTMLButtonElement,
  StartOnlyFansButtonProps
>(
  (
    { className, creator, openProxyConfigurationDialog, children, ...props },
    ref,
  ) => {
    const toast = useToast()
    const { isApp, startedSessions } = useDesktopApp()
    const [isCooldown, setIsCooldown] = useState(false)
    const [isWarningOpen, setWarningOpen] = useState(false)
    const [checkingProxy, setCheckingProxy] = useState(false)
    const [checkProxyError, setCheckProxyError] = useState<string | undefined>(
      undefined,
    )
    const enableButtonCooldown = useCallback(() => {
      setIsCooldown(true)

      setTimeout(() => {
        setIsCooldown(false)
      }, 1000)
    }, [])

    const disabledButton = (tooltip: string) => (
      <Tooltip
        content={tooltip}
        className="w-full"
      >
        <button
          className={classNames(className, 'h-[32px] opacity-60')}
          ref={ref}
          {...props}
          onClick={(event) => {
            event.preventDefault()
            window.open('https://www.creatorhero.com/download', '_blank')
          }}
        >
          <OfIcon />
          Start OnlyFans
        </button>
      </Tooltip>
    )

    if (!isActiveCreator(creator)) {
      return disabledButton('This creator is not active.')
    }

    if (
      !creator.permissions.includes(CreatorPermission.ACCESS_ONLYFANS_PROFILE)
    ) {
      return disabledButton(
        "You don't have the permission to access this OnlyFans profile.",
      )
    }

    if (!isApp) {
      return disabledButton(
        'Download the desktop app to start up the OnlyFans profile',
      )
    }

    const session = [...startedSessions.values()].find(
      (session) =>
        session.creatorId === creator.id &&
        (session.status === 'Running' || session.status === 'Starting'),
    )
    const isStopButton = session && session.status === 'Running'

    const handleProfileStart = () => {
      startOnlyFansProfile(
        creator,
        openProxyConfigurationDialog,
        setCheckingProxy,
        setCheckProxyError,
        enableButtonCooldown,
      )
    }

    return (
      <>
        <CheckProxyError
          creator={creator}
          checkProxyError={checkProxyError}
          closeDialog={() => setCheckProxyError(undefined)}
          enableButtonCooldown={enableButtonCooldown}
          openProxyConfigurationDialog={openProxyConfigurationDialog}
        />
        <button
          disabled={
            !isStopButton &&
            (checkingProxy ||
              isCooldown ||
              (session && session.status === 'Starting'))
          }
          className={classNames(
            className,
            isStopButton ? 'bg-danger-default hover:bg-danger-default/70' : '',
            'h-[32px]',
          )}
          ref={ref}
          {...props}
          onClick={() => {
            if (!window.electronAPI) {
              toast.error('Electron API not found.')

              return
            }

            if (isStopButton) {
              sendStartSession(creator)
              enableButtonCooldown()
            } else {
              setWarningOpen(true)
            }
          }}
        >
          <OfIcon />
          {isStopButton ? 'Stop OnlyFans' : 'Start OnlyFans'}
        </button>
        <StartOfWarningDialog
          loading={
            checkingProxy ||
            isCooldown ||
            !!(session && session.status === 'Starting')
          }
          open={isWarningOpen}
          onClose={() => setWarningOpen(false)}
          onStart={handleProfileStart}
        />
      </>
    )
  },
)

StartOnlyFansButton.displayName = 'StartOnlyFansButton'

interface CheckProxyErrorProps {
  checkProxyError: string | undefined
  closeDialog: () => void
  enableButtonCooldown: () => void
  creator: Creator
  openProxyConfigurationDialog: (firstLaunch: boolean) => void
}

const CheckProxyError = ({
  creator,
  checkProxyError,
  closeDialog,
  enableButtonCooldown,
  openProxyConfigurationDialog,
}: CheckProxyErrorProps) => (
  <Dialog
    show={!!checkProxyError}
    onClose={closeDialog}
  >
    <Dialog.Header>
      <Dialog.Title>Proxy connection failed</Dialog.Title>
      <Dialog.Description>
        <p className="mb-4">
          There was an error connecting to the proxy server. Make sure you have
          a network connection and that the proxy settings are correct.
        </p>
        {checkProxyError && (
          <pre className="text-title max-h-72 overflow-y-scroll rounded-md bg-gray-700/20 p-2 text-xs dark:bg-gradient-to-b dark:from-gray-700/40 dark:to-gray-700/20">
            <code>{checkProxyError.replaceAll('\\n', '\n')}</code>
          </pre>
        )}
        <p className="mt-4">
          You can start the browser anyway and it will try to use the proxy
          internally. If this is a proxy provided by CreatorHero, send the
          support a screenshot of this and they will replace it.
        </p>
      </Dialog.Description>
    </Dialog.Header>
    <Dialog.Footer className="mt-6 grid grid-cols-2 gap-4">
      <Button
        onClick={() => {
          closeDialog()
          openProxyConfigurationDialog(false)
        }}
      >
        Open Proxy Configuration
      </Button>
      <Button
        variant="destructive"
        onClick={() => {
          closeDialog()
          sendStartSession(creator)
          enableButtonCooldown()
        }}
      >
        Start anyway
      </Button>
    </Dialog.Footer>
  </Dialog>
)
